import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { StepNavigation } from "../components/ui/StepNavigation";
import GeneralInfo from "../components/pvcurtailment/GeneralInfo";
import AssetSpec from "../components/pvcurtailment/AssetSpec";
import ContractPar from "../components/pvcurtailment/ContractPar";
import ContractProp from "../components/pvcurtailment/ContractProp";
import { toastConfig } from "../components/config/toast.config";
import { RootState } from "../store/indexRedux";
import { resetStep1, resetApiData, clearAllPvCurtailmentCache, PV_STORAGE_KEYS } from "../store/pvCurtailment";
import "react-toastify/dist/ReactToastify.css";

function PvCurltaiment() {
  const dispatch = useDispatch();
  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);

  const getCurrentCompanyId = () => {
    return (
      selectedCompany?.company_id?.toString() ||
      JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id?.toString() ||
      null
    );
  };

  const checkCompanyChanged = () => {
    const currentCompanyId = getCurrentCompanyId();
    if (!currentCompanyId) return false;
    const savedCompanyId = localStorage.getItem(PV_STORAGE_KEYS.companyId);
    return savedCompanyId && savedCompanyId !== currentCompanyId;
  };

  const resetWizard = useCallback(() => {
    dispatch(resetStep1());
    dispatch(resetApiData());
    clearAllPvCurtailmentCache();
    setCurrentStep(1);
    setHighestStepReached(1);
  }, [dispatch]);

  // A restored position is only trustworthy if the form data behind it still
  // exists — the data key and the wizard key can diverge (#466): step 2+ needs
  // an injection selection, step 3+ needs the contract start date.
  const maxRestorableStep = () => {
    try {
      const step1 = JSON.parse(localStorage.getItem(PV_STORAGE_KEYS.step1) || "{}");
      if (!step1.injectionEAN) return 1;
      if (!step1.desiredStartDate) return 2;
      return 4;
    } catch {
      return 1;
    }
  };

  const [currentStep, setCurrentStep] = useState<number>(() => {
    if (checkCompanyChanged()) return 1;
    try {
      const cached = JSON.parse(localStorage.getItem(PV_STORAGE_KEYS.wizard) || "{}");
      const cs = Number(cached?.currentStep);
      if (!Number.isInteger(cs) || cs < 1 || cs > 4) return 1;
      return Math.min(cs, maxRestorableStep());
    } catch {
      return 1;
    }
  });

  const [pvCurtailmentHardStop, setPvCurtailmentHardStop] = useState(false);

  const [highestStepReached, setHighestStepReached] = useState<number>(() => {
    if (checkCompanyChanged()) return 1;
    try {
      const cached = JSON.parse(localStorage.getItem(PV_STORAGE_KEYS.wizard) || "{}");
      const hr = Number(cached?.highestStepReached);
      if (!Number.isInteger(hr) || hr < 1 || hr > 4) return 1;
      return Math.min(hr, maxRestorableStep());
    } catch {
      return 1;
    }
  });

  useEffect(() => {
    const currentCompanyId =
      selectedCompany?.company_id?.toString() ||
      JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id?.toString() ||
      null;

    if (currentCompanyId) {
      const savedCompanyId = localStorage.getItem(PV_STORAGE_KEYS.companyId);

      if (savedCompanyId && savedCompanyId !== currentCompanyId) {
        resetWizard();
      }

      localStorage.setItem(PV_STORAGE_KEYS.companyId, currentCompanyId);
    } else {
      resetWizard();
    }
  }, [selectedCompany?.company_id, dispatch, resetWizard]);

  useEffect(() => {
    localStorage.setItem(PV_STORAGE_KEYS.wizard, JSON.stringify({ currentStep, highestStepReached }));
  }, [currentStep, highestStepReached]);

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber <= highestStepReached) {
      setCurrentStep(stepNumber);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (nextStep > highestStepReached) {
        setHighestStepReached(nextStep);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleResetProgress = useCallback(() => {
    setHighestStepReached(1);
  }, []);

  const handleSubmit = () => {
    resetWizard();
  };

  const steps = [
    { number: 1, label: "General information" },
    { number: 2, label: "Asset specifications" },
    { number: 3, label: "Contract parameters" },
    { number: 4, label: "Contract proposal" },
  ];

  return (
    <div className="mb-24">
      <ToastContainer {...toastConfig} />
      <div className="mt-20">
        <StepNavigation
          steps={steps}
          currentStep={currentStep}
          highestStepReached={pvCurtailmentHardStop ? 1 : highestStepReached}
          onStepClick={handleStepClick}
        />
      </div>

      <div className="lg:w-[80%] p-8 mt-[6rem] mx-auto">
        <div className="bg-gray-50 dark:bg-transparent rounded-lg">
          {currentStep === 1 && <GeneralInfo onNext={handleNext} onPrevious={handlePrevious} onResetProgress={handleResetProgress} onHardStop={setPvCurtailmentHardStop} />}
          {currentStep === 2 && <AssetSpec onNext={handleNext} onPrevious={handlePrevious} />}
          {currentStep === 3 && <ContractPar onNext={handleNext} onPrevious={handlePrevious} />}
          {currentStep === 4 && <ContractProp onSubmit={handleSubmit} onPrevious={handlePrevious} />}
        </div>
      </div>
    </div>
  );
}

export default PvCurltaiment;
